import React, { ReactElement, ReactNode } from 'react'
import { Subject } from 'rxjs'
import SnackbarMessage, { DisplayType } from '../Components/SnackbarMessage'
import MoreDetailsType from '../Components/SnackbarMessage/Types'
import { messageType } from '../Types/Types'
import { $akixi } from '../../../NonReact/common/AkixiWindow'

enum MessageDuration {
    SUCCESS = 15000,
    DEFAULT = 50000
}

const displayMessage = (
    message: messageType,
    type: DisplayType,
    duration?: number,
    details?: ReactNode | string,
    internalCloseTrigger?: Subject<void>
): Voidable<boolean> => {
    // eslint-disable-next-line
    const snackbarKey: number = $akixi?.enqueueSnackbar?.(null, {
        content: (key: string): ReactElement => (
            <div key={key}>
                <SnackbarMessage displayType={type} snackElementReference={key} message={message} details={details} />
            </div>
        ),
        autoHideDuration: duration || MessageDuration.DEFAULT
    })

    internalCloseTrigger?.subscribe((): void => {
        // eslint-disable-next-line
        $akixi?.closeSnackbar(snackbarKey)
    })
}

/**
 * Display an success message in the snackbar.
 *
 * @param message - The message to display.
 */
export const displaySuccessMessage = (
    message: messageType,
    details?: MoreDetailsType,
    internalCloseTrigger?: Subject<void>
): Voidable<boolean> => {
    displayMessage(message, DisplayType.Success, MessageDuration.SUCCESS, details, internalCloseTrigger)
}

/**
 * Display an info message in the snackbar.
 *
 * @param message - The message to display.
 */
export const displayInfoMessage = (
    message: messageType,
    details?: MoreDetailsType,
    internalCloseTrigger?: Subject<void>
): Voidable<boolean> => {
    displayMessage(message, DisplayType.Information, undefined, details, internalCloseTrigger)
}

/**
 * Display a warning message in the snackbar.
 *
 * @param message - The message to display.
 */
export const displayWarningMessage = (
    message: messageType,
    details?: MoreDetailsType,
    internalCloseTrigger?: Subject<void>
): Voidable<boolean> => {
    displayMessage(message, DisplayType.Warning, undefined, details, internalCloseTrigger)
}

/**
 * Display an error message in the snackbar.
 *
 * @param message - The message to display.
 */
export const displayErrorMessage = (
    message: messageType,
    details?: MoreDetailsType,
    internalCloseTrigger?: Subject<void>
): Voidable<boolean> => {
    displayMessage(message, DisplayType.Error, undefined, details, internalCloseTrigger)
}

export const displayError = (pError: Error): void => {
    displayMessage(pError.message, DisplayType.Error)
}
